import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Printer, Eye } from "lucide-react";
import { Bill } from "@/types";
import { format } from "date-fns";
import { useState } from "react";
import { BillViewerDialog } from "./BillViewerDialog";

interface BillHistoryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  bills: Bill[];
  patientName: string;
}

export function BillHistoryDialog({ isOpen, onClose, bills, patientName }: BillHistoryDialogProps) {
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const handleViewBill = (bill: Bill) => {
    setSelectedBill(bill);
    setIsViewerOpen(true);
  };

  const handlePrintBill = (bill: Bill) => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      window.print();
      return;
    }

    // Create the print HTML for the specific bill
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Bill - ${bill.billNumber}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 0;
              padding: 10px 20px;
              font-size: 12px;
              line-height: 1.4;
              color: #000;
              background: white;
            }
            
            .bill-header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #000;
            }
            
            .clinic-name {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .clinic-details {
              font-size: 11px;
              color: #666;
              margin-bottom: 5px;
            }
            
            .patient-bill-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 11px;
            }
            
            .patient-info {
              flex: 1;
              margin-right: 20px;
            }
            
            .bill-info {
              flex: 1;
              text-align: right;
            }
            
            .services-section {
              margin-bottom: 20px;
            }
            
            .service-header {
              display: grid;
              grid-template-columns: 2fr 1fr auto;
              gap: 15px;
              font-weight: bold;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #ccc;
            }
            
            .service-item {
              display: grid;
              grid-template-columns: 2fr 1fr auto;
              gap: 15px;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .service-item > div:nth-child(2) {
              text-align: left;
              min-width: 80px;
            }
            
            .total-section {
              margin-top: 20px;
              border-top: 1px solid #ccc;
              padding-top: 10px;
            }
            
            .total-row {
              display: flex;
              justify-content: space-between;
              margin-bottom: 5px;
              font-size: 11px;
            }
            
            .total-row.final {
              font-weight: bold;
              font-size: 12px;
              border-top: 1px solid #000;
              padding-top: 5px;
              margin-top: 10px;
            }
            
            .footer {
              text-align: center;
              margin-top: 30px;
              padding-top: 10px;
              border-top: 1px solid #ccc;
              font-size: 10px;
              color: #666;
            }
            
            .amount {
              text-align: right;
            }
            
            .due-amount {
              color: #dc2626;
              font-weight: bold;
            }
            
            .fully-paid {
              color: #16a34a;
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="bill-header">
            <div class="clinic-name">${bill.clinicDetails.name}</div>
            <div class="clinic-details">${bill.clinicDetails.address}</div>
            <div class="clinic-details">Phone: ${bill.clinicDetails.phone} | Email: ${bill.clinicDetails.email}</div>
            ${bill.clinicDetails.website ? `<div class="clinic-details">Website: ${bill.clinicDetails.website}</div>` : ''}
          </div>
          
          <div class="patient-bill-info">
            <div class="patient-info">
              <div><span class="label">Name:</span> ${patientName}</div>
              <div><span class="label">Bill No:</span> ${bill.billNumber}</div>
            </div>
            <div class="bill-info">
              <div><span class="label">Date:</span> ${format(new Date(bill.billDate), "dd MMM yyyy")}</div>
            </div>
          </div>
          
          <div class="services-section">
            <div class="service-header">
              <div>Service</div>
              <div></div>
              <div class="amount">Amount (₹)</div>
            </div>
            ${bill.services.map((service) => `
              <div class="service-item">
                <div>${service.service_name}</div>
                <div>${format(new Date(service.added_at), 'dd MMM yyyy')}</div>
                <div class="amount">${(Number(service.price) || 0).toFixed(2)}</div>
              </div>
            `).join('')}
          </div>
          
          <div class="total-section">
            <div class="total-row">
              <span>Total Amount</span>
              <span class="amount">₹${bill.totalAmount.toFixed(2)}</span>
            </div>
            ${bill.discountAmount > 0 ? `
              <div class="total-row">
                <span>Discount</span>
                <span class="amount">-₹${bill.discountAmount.toFixed(2)}</span>
              </div>
            ` : ''}
            <div class="total-row final">
              <span>Final Total</span>
              <span class="amount">₹${(bill.totalAmount - bill.discountAmount).toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Amount Paid</span>
              <span class="amount">₹${bill.amountPaid.toFixed(2)}</span>
            </div>
            <div class="total-row">
              <span>Payment Mode</span>
              <span class="amount">${bill.paymentMode}</span>
            </div>
            <div class="total-row final ${bill.amountPaid >= (bill.totalAmount - bill.discountAmount) ? 'fully-paid' : 'due-amount'}">
              <span>${bill.amountPaid >= (bill.totalAmount - bill.discountAmount) ? 'Fully Paid' : 'Due Amount'}</span>
              <span class="amount">₹${Math.abs((bill.totalAmount - bill.discountAmount) - bill.amountPaid).toFixed(2)}</span>
            </div>
          </div>
          
          <div class="footer">
            <p>Thank you for choosing ${bill.clinicDetails.name}</p>
            <p>This is a computer generated receipt</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    
    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const getStatusBadge = (bill: Bill) => {
    const isFullyPaid = bill.amountPaid >= (bill.totalAmount - bill.discountAmount);
    
    if (bill.status === 'cancelled') {
      return <Badge variant="destructive">Cancelled</Badge>;
    } else if (bill.status === 'refunded') {
      return <Badge variant="secondary">Refunded</Badge>;
    } else if (isFullyPaid) {
      return <Badge variant="default">Paid</Badge>;
    } else {
      return <Badge variant="outline">Pending</Badge>;
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bill History - {patientName}</DialogTitle>
            <DialogDescription>
              View and manage all bills for this patient
            </DialogDescription>
          </DialogHeader>
          
          {bills.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No bills found for this patient.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell>Bill Number</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Services</TableCell>
                    <TableCell>Total Amount</TableCell>
                    <TableCell>Amount Paid</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((bill) => (
                    <TableRow key={bill.id}>
                      <TableCell className="font-medium">{bill.billNumber}</TableCell>
                      <TableCell>{format(new Date(bill.billDate), "dd MMM yyyy")}</TableCell>
                      <TableCell>{bill.services.length} service(s)</TableCell>
                      <TableCell>₹{(bill.totalAmount - bill.discountAmount).toFixed(2)}</TableCell>
                      <TableCell>₹{bill.amountPaid.toFixed(2)}</TableCell>
                      <TableCell>{getStatusBadge(bill)}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewBill(bill)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePrintBill(bill)}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {selectedBill && (
        <BillViewerDialog
          isOpen={isViewerOpen}
          onClose={() => {
            setIsViewerOpen(false);
            setSelectedBill(null);
          }}
          bill={selectedBill}
          patientName={patientName}
        />
      )}
    </>
  );
} 